import type {Pointer, PossibleViewModes, PS} from '@wix/document-services-types'
import _ from 'lodash'
import constants from '../../constants/constants'
import structure from '../../structure/structure'
import {coreUtils} from '@wix/santa-ds-libs'
const {masterPageLayoutUtils} = coreUtils
import sectionsLayoutMigrator from '../../sectionsLayoutMigrator/sectionsLayoutMigrator'
import isPageAnchorsRemoved from './utils/isPageAnchorsRemoved'
import layoutSettingsUtils from '../../structure/utils/layoutSettingsUtils'
import type {DsFixer} from '../dsDataFixers'

const getHeader = (ps: PS, viewMode: PossibleViewModes) => ps.pointers.full.components.getHeader(viewMode)

const getPagesContainer = (ps: PS, viewMode: PossibleViewModes) =>
    ps.pointers.full.components.getPagesContainer(viewMode)

const getCompLayout = (ps: PS, compPointer: Pointer) =>
    ps.dal.full.get(ps.pointers.getInnerPointer(compPointer, ['layout']))

const getCompTop = (ps: PS, compPointer) => getCompLayout(ps, compPointer).y

const isFixedPosition = (ps: PS, compPointer) => _.get(getCompLayout(ps, compPointer), 'fixedPosition', false)

const getCompBottom = (ps: PS, compPointer) => {
    const compLayout = getCompLayout(ps, compPointer)

    return compLayout.y + compLayout.height
}

const canRunFixer = (ps: PS) =>
    isPageAnchorsRemoved(ps, ps.pointers.full.components.getMasterPage(constants.VIEW_MODES.DESKTOP))

const getMasterPageDataItemPointer = (ps: PS) => ps.pointers.data.getDataItemFromMaster('masterPage')
const isSiteAlreadyFixed = (ps: PS) => layoutSettingsUtils.getLayoutSettings(ps).soapCompsAroundPagesContainer

const shouldRunFixer = (ps: PS) => !isSiteAlreadyFixed(ps)

const flagSiteAsFixed = (ps: PS) => {
    const masterPageDataItemPointer = getMasterPageDataItemPointer(ps)
    const masterPageDataItem = ps.dal.full.get(masterPageDataItemPointer)
    ps.dal.full.set(
        masterPageDataItemPointer,
        _.merge({}, masterPageDataItem, {
            layoutSettings: {
                soapCompsAroundPagesContainer: true
            }
        })
    )
}

const getCompType = (ps: PS, comp) => ps.dal.full.get(ps.pointers.getInnerPointer(comp, 'componentType'))

const isMasterPageSection = (ps: PS, comp) =>
    _.includes(masterPageLayoutUtils.MASTER_PAGES_SECTIONS[comp.type], getCompType(ps, comp))

const isPagesContainer = (ps: PS, comp) =>
    ps.pointers.components.isSameComponent(comp, getPagesContainer(ps, comp.type))

const filterSoapAndPagesContainer = (ps: PS, components) =>
    _.reject(
        components,
        comp => (isMasterPageSection(ps, comp) && !isPagesContainer(ps, comp)) || isFixedPosition(ps, comp)
    )

const filterSoap = (ps: PS, components) => _.filter(components, comp => !isMasterPageSection(ps, comp))

const getChildren = (ps: PS, container) => ps.pointers.full.components.getChildren(container)

const getCompIndex = (ps: PS, components: Pointer[], target) =>
    _.findIndex(components, comp => ps.pointers.components.isSameComponent(comp, target))

const getNewMasterPageChildrenOrder = (ps: PS, masterPage) => {
    const masterPageChildren = getChildren(ps, masterPage)
    const masterPageSectionsAndFixedSoap: Pointer[] = _.filter(
        masterPageChildren,
        comp => isMasterPageSection(ps, comp) || isFixedPosition(ps, comp)
    ) as Pointer[]
    const pagesContainerIndex = getCompIndex(ps, masterPageSectionsAndFixedSoap, getPagesContainer(ps, masterPage.type))

    return [
        ...masterPageSectionsAndFixedSoap.slice(0, pagesContainerIndex),
        ...filterSoapAndPagesContainer(ps, masterPageChildren),
        ...masterPageSectionsAndFixedSoap.slice(pagesContainerIndex + 1, masterPageSectionsAndFixedSoap.length)
    ]
}

const isBehind = (ps: PS, components, compA, compB) =>
    getCompIndex(ps, components, compA) < getCompIndex(ps, components, compB)

function moveChildrenFromEndToStart(ps: PS, container, numberOfChildren) {
    const childrenContainerPointer = ps.pointers.full.components.getChildrenContainer(container)
    const childrenComps = ps.dal.full.get(childrenContainerPointer)
    const index = childrenComps.length - numberOfChildren
    const newChildrenComps = [...childrenComps.slice(index, childrenComps.length), ...childrenComps.slice(0, index)]

    ps.dal.full.set(childrenContainerPointer, newChildrenComps)
}

const attachToBack = (ps: PS, comps, newParent) => {
    if (_.isEmpty(comps)) {
        return
    }
    attachToFront(ps, comps, newParent)

    moveChildrenFromEndToStart(ps, newParent, comps.length)
}

const canAttachToContainer = (ps: PS, comp, newParent) => structure.canSetContainer(ps, comp, newParent)

const attachToFront = (ps: PS, comps, newParent) =>
    _.forEach(comps, comp => structure.setContainer(ps, comp, comp, newParent))

const getSoapComponents = (ps: PS, viewMode) =>
    filterSoap(ps, getChildren(ps, ps.pointers.full.components.getMasterPage(viewMode)))

const attachMovingCompsToHeader = (ps: PS, viewMode, masterPageChildrenOrder, newMasterPageChildrenOrder) => {
    const header = getHeader(ps, viewMode)

    const isOverlappedWithHeader = comp => getCompTop(ps, comp) < getCompBottom(ps, header)
    if (!header || isFixedPosition(ps, header)) {
        return []
    }

    const soapComps = getSoapComponents(ps, viewMode)
    const candidatesToAttachToHeader = _.filter(
        soapComps,
        comp => !isFixedPosition(ps, comp) && canAttachToContainer(ps, comp, header) && isOverlappedWithHeader(comp)
    )
    const {attachToBack: compsToAttachToBack, attachToFront: compsToAttachToFront} = _.reduce(
        candidatesToAttachToHeader,
        (result, comp) => {
            const wasBehindSection = isBehind(ps, masterPageChildrenOrder, comp, header)
            const isBehindSection = isBehind(ps, newMasterPageChildrenOrder, comp, header)

            if (wasBehindSection && !isBehindSection) {
                result.attachToBack.push(comp)
            } else if (!wasBehindSection && isBehindSection) {
                result.attachToFront.push(comp)
            }

            return result
        },
        {attachToBack: [], attachToFront: []}
    )

    attachToBack(ps, compsToAttachToBack, header)
    attachToFront(ps, compsToAttachToFront, header)

    return compsToAttachToBack.concat(compsToAttachToFront)
}

const reorderSOAP = (ps: PS, masterPage, newMasterPageChildrenOrder) => {
    const childrenContainerPointer = ps.pointers.full.components.getChildrenContainer(masterPage)
    const children = ps.dal.full.get(childrenContainerPointer)
    const newChildren = _.sortBy(children, compId => _.findIndex(newMasterPageChildrenOrder, {id: compId}))

    ps.dal.full.set(childrenContainerPointer, newChildren)
}

const fixMasterPage = (ps: PS) => {
    const viewMode = constants.VIEW_MODES.DESKTOP
    const masterPage = ps.pointers.full.components.getMasterPage(viewMode)
    const masterPageChildrenOrder = getChildren(ps, masterPage)
    const newMasterPageChildrenOrder = getNewMasterPageChildrenOrder(ps, masterPage)

    if (!_.isEqual(masterPageChildrenOrder, newMasterPageChildrenOrder)) {
        attachMovingCompsToHeader(ps, viewMode, masterPageChildrenOrder, newMasterPageChildrenOrder)
        reorderSOAP(ps, masterPage, newMasterPageChildrenOrder)
    }
}

const exec = (ps: PS) => {
    if (!shouldRunFixer(ps) || !canRunFixer(ps)) {
        return false
    }

    sectionsLayoutMigrator(ps)
    fixMasterPage(ps)

    flagSiteAsFixed(ps)
}

const fixer: DsFixer = {
    exec,
    name: 'soapOrderFixer',
    version: 1,
    fixerRequiresReruns: true
}
export default fixer
